import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { AlertTriangle, Flame, Info, RotateCcw } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";
import { NumberInput } from "@/components/calculator/NumberInput";
import { UnitToggle } from "@/components/calculator/UnitToggle";
import { CopyResultButton } from "@/components/calculator/CopyResultButton";
import { ShareButton } from "@/components/calculator/ShareButton";
import { Button } from "@/components/ui/button";
import { useAppStore } from "@/lib/store";
import { toBengaliNumerals } from "@/lib/bdNumerals";
import { buildSeo } from "@/lib/seo";
import { cn } from "@/lib/utils";

import {
  ACTIVITY_MULTIPLIER,
  computeCalories,
  validate,
  type Activity,
  type Formula,
  type Gender,
  type Goal,
  type WarningKey,
} from "@/lib/calculators/calorie";

import {
  KG_PER_LB,
  CM_PER_IN,
  inchesToFtIn,
  cmToIn,
  kgToLb,
  lbToKg,
  inToCm,
} from "@/lib/calculators/bmi";

type UnitSystem = "metric" | "imperial";
type Bilingual = { en: string; bn: string };

const STR = {
  h1: { en: "Calorie Calculator", bn: "ক্যালরি ক্যালকুলেটর" },
  desc: {
    en: "Calculate daily calorie needs (TDEE), BMR and macronutrients for weight loss, gain or maintenance — built for Bangladesh.",
    bn: "ওজন কমানো, বাড়ানো বা ধরে রাখার জন্য দৈনিক ক্যালরি (TDEE), বিএমআর ও ম্যাক্রো হিসাব করুন — বাংলাদেশের জন্য তৈরি।",
  },

  units: { en: "Units", bn: "একক" },
  metric: { en: "Metric", bn: "মেট্রিক" },
  imperial: { en: "Imperial", bn: "ইম্পেরিয়াল" },

  gender: { en: "Gender", bn: "লিঙ্গ" },
  male: { en: "Male", bn: "পুরুষ" },
  female: { en: "Female", bn: "মহিলা" },

  age: { en: "Age", bn: "বয়স" },
  ageUnit: { en: "years", bn: "বছর" },

  height: { en: "Height", bn: "উচ্চতা" },
  cm: { en: "cm", bn: "সেমি" },
  ft: { en: "ft", bn: "ফিট" },
  inch: { en: "in", bn: "ইঞ্চি" },

  weight: { en: "Weight", bn: "ওজন" },
  kg: { en: "kg", bn: "কেজি" },
  lb: { en: "lb", bn: "পাউন্ড" },

  activity: { en: "Activity Level", bn: "শারীরিক সক্রিয়তা" },
  goal: { en: "Goal", bn: "লক্ষ্য" },
  formula: { en: "Formula", bn: "সূত্র" },
  bodyFat: { en: "Body fat %", bn: "চর্বি %" },

  reset: { en: "Reset", bn: "রিসেট" },

  daily: { en: "Daily Calorie Need", bn: "দৈনিক ক্যালরি প্রয়োজন" },
  kcal: { en: "kcal/day", bn: "কিলোক্যালরি/দিন" },
  bmr: { en: "BMR (resting)", bn: "বিএমআর (বিশ্রামে)" },
  tdee: { en: "TDEE (with activity)", bn: "TDEE (সক্রিয়তা সহ)" },
  activityKcal: { en: "Activity calories", bn: "সক্রিয়তা ক্যালরি" },

  macros: { en: "Macronutrients", bn: "ম্যাক্রোনিউট্রিয়েন্ট" },
  carbs: { en: "Carbohydrates", bn: "শর্করা" },
  protein: { en: "Protein", bn: "প্রোটিন" },
  fat: { en: "Fat", bn: "চর্বি" },
  grams: { en: "g", bn: "গ্রাম" },
  pct: { en: "%", bn: "%" },

  water: { en: "Recommended water", bn: "প্রস্তাবিত পানি" },
  waterNote: {
    en: "(+0.5 L for hot/humid Bangladesh climate)",
    bn: "(বাংলাদেশের গরম/আর্দ্র আবহাওয়ায় অতিরিক্ত +০.৫ লিটার)",
  },

  disclaimer: {
    en: "This calculator gives general estimates based on standard formulas. It is not medical advice. Consult a registered dietitian or doctor before significant dietary changes.",
    bn: "এই ক্যালকুলেটর সাধারণ অনুমান দেয় — চিকিৎসা পরামর্শ নয়। খাদ্যাভ্যাসে বড় পরিবর্তনের আগে অবশ্যই একজন ডায়েটিশিয়ান বা ডাক্তারের পরামর্শ নিন।",
  },

  err_age: { en: "Age must be 15–100.", bn: "বয়স ১৫–১০০ এর মধ্যে হতে হবে।" },
  err_height: { en: "Height seems incorrect.", bn: "উচ্চতা সঠিক মনে হচ্ছে না।" },
  err_weight: { en: "Weight seems incorrect.", bn: "ওজন সঠিক মনে হচ্ছে না।" },
  err_bf: { en: "Enter body fat % between 3 and 60.", bn: "চর্বি % ৩ থেকে ৬০ এর মধ্যে দিন।" },
};

const ACTIVITY_LABELS: Record<Activity, { name: Bilingual; desc: Bilingual; mult: number }> = {
  sedentary: {
    name: { en: "Sedentary", bn: "নিষ্ক্রিয়" },
    desc: { en: "Desk job, little/no exercise", bn: "ডেস্ক জব, ব্যায়াম নেই" },
    mult: 1.2,
  },
  light: {
    name: { en: "Lightly Active", bn: "হালকা সক্রিয়" },
    desc: { en: "Light exercise 1–3 days/week", bn: "সপ্তাহে ১–৩ দিন হালকা ব্যায়াম" },
    mult: 1.375,
  },
  moderate: {
    name: { en: "Moderately Active", bn: "মাঝারি সক্রিয়" },
    desc: { en: "Moderate exercise 3–5 days/week", bn: "সপ্তাহে ৩–৫ দিন মাঝারি ব্যায়াম" },
    mult: 1.55,
  },
  very: {
    name: { en: "Very Active", bn: "খুব সক্রিয়" },
    desc: { en: "Hard exercise 6–7 days/week", bn: "সপ্তাহে ৬–৭ দিন কঠিন ব্যায়াম" },
    mult: 1.725,
  },
  extra: {
    name: { en: "Extra Active", bn: "অতি সক্রিয়" },
    desc: { en: "Hard exercise + physical job", bn: "কঠিন ব্যায়াম + শারীরিক কাজ" },
    mult: 1.9,
  },
};

const GOAL_LABELS: Record<Goal, Bilingual> = {
  maintain: { en: "Maintain weight", bn: "ওজন বজায় রাখা" },
  mild_loss: { en: "Mild loss (~0.25 kg/week)", bn: "হালকা কমানো (~০.২৫ কেজি/সপ্তাহ)" },
  moderate_loss: { en: "Moderate loss (~0.5 kg/week)", bn: "মাঝারি কমানো (~০.৫ কেজি/সপ্তাহ)" },
  mild_gain: { en: "Mild gain (~0.25 kg/week)", bn: "হালকা বাড়ানো (~০.২৫ কেজি/সপ্তাহ)" },
  moderate_gain: { en: "Moderate gain (~0.5 kg/week)", bn: "মাঝারি বাড়ানো (~০.৫ কেজি/সপ্তাহ)" },
};

const FORMULA_LABELS: Record<Formula, Bilingual> = {
  mifflin: { en: "Mifflin-St Jeor (default)", bn: "Mifflin-St Jeor (ডিফল্ট)" },
  harris: { en: "Harris-Benedict", bn: "Harris-Benedict" },
  katch: { en: "Katch-McArdle (needs body fat %)", bn: "Katch-McArdle (চর্বি % দরকার)" },
};

const WARNING_TEXT: Record<WarningKey, Bilingual> = {
  below_safety_floor: {
    en: "Calorie target below safe minimum (1,200 kcal F / 1,500 kcal M). Floor applied. Consult a dietitian.",
    bn: "ক্যালরি লক্ষ্য নিরাপদ সীমার নিচে (নারী ১,২০০ / পুরুষ ১,৫০০)। সীমা প্রয়োগ করা হয়েছে। ডায়েটিশিয়ানের পরামর্শ নিন।",
  },
  underweight_loss: {
    en: "Your BMI is in the underweight range. Weight loss is not recommended without medical supervision.",
    bn: "আপনার বিএমআই কম ওজনের পরিসরে। চিকিৎসকের তত্ত্বাবধান ছাড়া ওজন কমানো ঠিক নয়।",
  },
  obese_gain: {
    en: "Your BMI is in the obese range. Consider consulting a doctor about weight management.",
    bn: "আপনার বিএমআই স্থূলতার পরিসরে। ওজন ব্যবস্থাপনার জন্য ডাক্তারের সঙ্গে আলোচনা করুন।",
  },
  adolescent: {
    en: "Calorie needs for adolescents (under 18) differ. Consult a pediatric dietitian.",
    bn: "১৮ বছরের কম বয়সীদের ক্যালরি প্রয়োজন আলাদা। শিশু-ডায়েটিশিয়ানের পরামর্শ নিন।",
  },
  katch_missing_bf: {
    en: "Katch-McArdle requires body fat %.",
    bn: "Katch-McArdle এর জন্য চর্বি % প্রয়োজন।",
  },
};

const FAQ: { q: Bilingual; a: Bilingual }[] = [
  {
    q: { en: "How many calories should I eat per day?", bn: "প্রতিদিন কত ক্যালরি খাওয়া উচিত?" },
    a: {
      en: "It depends on age, sex, height, weight, activity and goal. Most adults need 1,800–2,500 kcal/day for maintenance. Use the calculator above for your personalised number.",
      bn: "এটি বয়স, লিঙ্গ, উচ্চতা, ওজন, সক্রিয়তা ও লক্ষ্যের উপর নির্ভর করে। অধিকাংশ প্রাপ্তবয়স্কের জন্য ১,৮০০–২,৫০০ কিলোক্যালরি প্রয়োজন। ব্যক্তিগত সংখ্যার জন্য উপরের ক্যালকুলেটর ব্যবহার করুন।",
    },
  },
  {
    q: { en: "What is BMR vs TDEE?", bn: "বিএমআর ও TDEE এর পার্থক্য কী?" },
    a: {
      en: "BMR (Basal Metabolic Rate) is the energy your body uses at complete rest. TDEE (Total Daily Energy Expenditure) is BMR plus calories burned through activity — it represents your true daily need.",
      bn: "বিএমআর হলো বিশ্রামে শরীর যে শক্তি ব্যবহার করে। TDEE হলো বিএমআর + সক্রিয়তায় খরচ হওয়া ক্যালরি — এটিই আপনার প্রকৃত দৈনিক প্রয়োজন।",
    },
  },
  {
    q: { en: "How do I lose weight safely?", bn: "নিরাপদভাবে ওজন কীভাবে কমাবো?" },
    a: {
      en: "Aim for a 250–500 kcal/day deficit, which gives ~0.25–0.5 kg per week. Faster loss risks muscle loss and nutrient deficiency. Combine with strength training and protein-rich meals.",
      bn: "প্রতিদিন ২৫০–৫০০ কিলোক্যালরি ঘাটতি রাখুন, যা সপ্তাহে ০.২৫–০.৫ কেজি কমাবে। দ্রুত কমালে পেশী ও পুষ্টি ক্ষতি হয়। শক্তি প্রশিক্ষণ ও প্রোটিন সমৃদ্ধ খাবার যুক্ত করুন।",
    },
  },
  {
    q: { en: "Which formula is most accurate?", bn: "কোন সূত্র সবচেয়ে নির্ভুল?" },
    a: {
      en: "Mifflin-St Jeor (1990) is widely considered the most accurate for the general population. Katch-McArdle is best for athletes who know their body fat %.",
      bn: "Mifflin-St Jeor (১৯৯০) সাধারণ মানুষের জন্য সবচেয়ে নির্ভুল ধরা হয়। যারা নিজেদের চর্বি % জানেন এমন খেলোয়াড়দের জন্য Katch-McArdle ভালো।",
    },
  },
  {
    q: { en: "ক্যালরি কী?", bn: "ক্যালরি কী?" },
    a: {
      en: "ক্যালরি হলো শক্তির একক। খাবার থেকে আমরা ক্যালরি পাই এবং দৈনন্দিন কাজে তা ব্যয় করি। ১ ক্যালরি = ১ গ্রাম পানির তাপমাত্রা ১°C বাড়ানোর শক্তি।",
      bn: "ক্যালরি হলো শক্তির একক। খাবার থেকে আমরা ক্যালরি পাই এবং দৈনন্দিন কাজে তা ব্যয় করি। ১ ক্যালরি = ১ গ্রাম পানির তাপমাত্রা ১°C বাড়ানোর শক্তি।",
    },
  },
  {
    q: { en: "প্রতিদিন কত ক্যালরি দরকার?", bn: "প্রতিদিন কত ক্যালরি দরকার?" },
    a: {
      en: "একজন প্রাপ্তবয়স্ক বাংলাদেশী পুরুষের জন্য সাধারণত ২,২০০–২,৫০০ এবং নারীর জন্য ১,৮০০–২,১০০ কিলোক্যালরি যথেষ্ট। সঠিক হিসাবের জন্য উপরে আপনার তথ্য দিন।",
      bn: "একজন প্রাপ্তবয়স্ক বাংলাদেশী পুরুষের জন্য সাধারণত ২,২০০–২,৫০০ এবং নারীর জন্য ১,৮০০–২,১০০ কিলোক্যালরি যথেষ্ট। সঠিক হিসাবের জন্য উপরে আপনার তথ্য দিন।",
    },
  },
  {
    q: { en: "Are calorie needs different for Bangladeshis?", bn: "বাংলাদেশীদের ক্যালরি কি আলাদা?" },
    a: {
      en: "South Asians generally have smaller frames and lower lean muscle mass on average, which slightly lowers BMR. Hot/humid climate also increases water needs. The formulas above remain valid but treat results as estimates.",
      bn: "দক্ষিণ এশীয়দের গড় দেহাবয়ব ছোট এবং পেশী ভর কম, তাই বিএমআর কিছুটা কম হয়। গরম/আর্দ্র আবহাওয়ায় পানির চাহিদা বাড়ে। উপরের সূত্র প্রযোজ্য — তবে ফলাফল অনুমান হিসাবে দেখুন।",
    },
  },
  {
    q: { en: "How much protein do I need?", bn: "প্রতিদিন কত প্রোটিন দরকার?" },
    a: {
      en: "0.8 g/kg for sedentary adults, 1.2–1.6 g/kg if active, and 1.6–2.2 g/kg for muscle gain or fat loss. The macro grid above uses these guidelines.",
      bn: "নিষ্ক্রিয়দের জন্য ০.৮ গ্রাম/কেজি, সক্রিয়দের জন্য ১.২–১.৬ গ্রাম/কেজি, পেশী বৃদ্ধি বা চর্বি কমানোর জন্য ১.৬–২.২ গ্রাম/কেজি। উপরের ম্যাক্রো গ্রিড এই নীতিই অনুসরণ করে।",
    },
  },
];

const BD_FOODS: { name: Bilingual; serving: Bilingual; kcal: number }[] = [
  { name: { en: "Plain rice", bn: "ভাত" }, serving: { en: "1 cup cooked", bn: "১ কাপ রান্না" }, kcal: 130 },
  { name: { en: "Roti / Ruti", bn: "রুটি" }, serving: { en: "1 medium (40g)", bn: "১ মাঝারি (৪০ গ্রাম)" }, kcal: 70 },
  { name: { en: "Dal (lentil)", bn: "ডাল" }, serving: { en: "1 cup", bn: "১ কাপ" }, kcal: 200 },
  { name: { en: "Egg (boiled)", bn: "ডিম (সিদ্ধ)" }, serving: { en: "1 large", bn: "১ বড়" }, kcal: 78 },
  { name: { en: "Chicken curry", bn: "মুরগির ঝোল" }, serving: { en: "100 g", bn: "১০০ গ্রাম" }, kcal: 220 },
  { name: { en: "Beef curry", bn: "গরুর মাংস" }, serving: { en: "100 g", bn: "১০০ গ্রাম" }, kcal: 290 },
  { name: { en: "Hilsa fish", bn: "ইলিশ মাছ" }, serving: { en: "100 g", bn: "১০০ গ্রাম" }, kcal: 210 },
  { name: { en: "Banana", bn: "কলা" }, serving: { en: "1 medium", bn: "১ মাঝারি" }, kcal: 105 },
  { name: { en: "Milk tea", bn: "দুধ চা" }, serving: { en: "1 cup", bn: "১ কাপ" }, kcal: 90 },
  { name: { en: "Singara", bn: "সিঙ্গারা" }, serving: { en: "1 piece", bn: "১ টি" }, kcal: 150 },
];

/* ------------ Route ------------ */

export const Route = createFileRoute("/calorie-calculator")({
  head: () => {
    const seo = buildSeo({
      title: "Calorie Calculator — Daily Needs for Bangladesh",
      description:
        "Free calorie calculator. Calculate daily calorie needs (TDEE), BMR, macronutrients for weight loss, gain or maintenance. Made for Bangladesh climate. ক্যালরি ক্যালকুলেটর।",
      path: "/calorie-calculator",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Calorie Calculator", path: "/calorie-calculator" },
      ],
      faq: FAQ.map((f) => ({ q: f.q.en, a: f.a.en })),
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: CalorieCalculatorPage,
});

/* ------------ Page ------------ */

const STORAGE_KEY = "bc.calorie.daily";

function CalorieCalculatorPage() {
  const lang = useAppStore((s) => s.lang);
  const tr = (k: keyof typeof STR) => (STR[k] as Bilingual)[lang];
  const num = (n: number | string) => (lang === "bn" ? toBengaliNumerals(n) : String(n));
  const fmt = (n: number) => num(n.toLocaleString("en-US"));

  const [unit, setUnit] = useState<UnitSystem>("metric");
  const [gender, setGender] = useState<Gender>("male");
  const [age, setAge] = useState("30");

  // Canonical metric
  const [heightCm, setHeightCm] = useState<number | null>(170);
  const [weightKg, setWeightKg] = useState<number | null>(65);

  // Display strings
  const [cmStr, setCmStr] = useState("170");
  const [kgStr, setKgStr] = useState("65");
  const [ft, setFt] = useState(() => String(inchesToFtIn(cmToIn(170)).ft));
  const [inch, setInch] = useState(() => String(Math.round(inchesToFtIn(cmToIn(170)).inch)));
  const [lb, setLb] = useState(() => kgToLb(65).toFixed(1));

  const [activity, setActivity] = useState<Activity>("moderate");
  const [goal, setGoal] = useState<Goal>("maintain");
  const [formula, setFormula] = useState<Formula>("mifflin");
  const [bodyFat, setBodyFat] = useState("20");

  /* sync metric strings */
  useEffect(() => {
    if (unit !== "metric") return;
    const h = parseFloat(cmStr);
    setHeightCm(Number.isFinite(h) ? h : null);
  }, [cmStr, unit]);
  useEffect(() => {
    if (unit !== "metric") return;
    const w = parseFloat(kgStr);
    setWeightKg(Number.isFinite(w) ? w : null);
  }, [kgStr, unit]);
  /* sync imperial */
  useEffect(() => {
    if (unit !== "imperial") return;
    const f = parseFloat(ft);
    const i = parseFloat(inch);
    if (Number.isFinite(f) || Number.isFinite(i)) {
      const totalIn = (Number.isFinite(f) ? f : 0) * 12 + (Number.isFinite(i) ? i : 0);
      setHeightCm(totalIn > 0 ? +inToCm(totalIn).toFixed(2) : null);
    } else setHeightCm(null);
  }, [ft, inch, unit]);
  useEffect(() => {
    if (unit !== "imperial") return;
    const p = parseFloat(lb);
    setWeightKg(Number.isFinite(p) ? +lbToKg(p).toFixed(2) : null);
  }, [lb, unit]);

  const switchUnit = (next: UnitSystem) => {
    if (next === unit) return;
    if (next === "imperial" && heightCm != null && weightKg != null) {
      const totalIn = cmToIn(heightCm);
      const { ft: f, inch: i } = inchesToFtIn(totalIn);
      setFt(String(f));
      setInch(String(Math.round(i)));
      setLb(kgToLb(weightKg).toFixed(1));
    } else if (next === "metric" && heightCm != null && weightKg != null) {
      setCmStr(String(Math.round(heightCm)));
      setKgStr(weightKg.toFixed(1).replace(/\.0$/, ""));
    }
    setUnit(next);
  };

  const ageNum = age.trim() === "" ? NaN : Number(age);
  const bfNum = bodyFat.trim() === "" ? NaN : Number(bodyFat);

  const validation = useMemo(
    () =>
      validate({
        gender,
        age: ageNum,
        heightCm: heightCm ?? undefined,
        weightKg: weightKg ?? undefined,
        activity,
        goal,
        formula,
        bodyFatPct: bfNum,
      }),
    [gender, ageNum, heightCm, weightKg, activity, goal, formula, bfNum],
  );

  const result = useMemo(() => {
    if (!validation.ok || heightCm == null || weightKg == null) return null;
    return computeCalories({
      gender,
      age: ageNum,
      heightCm,
      weightKg,
      activity,
      goal,
      formula,
      bodyFatPct: formula === "katch" ? bfNum : undefined,
    });
  }, [validation.ok, gender, ageNum, heightCm, weightKg, activity, goal, formula, bfNum]);

  // Persist daily target
  useEffect(() => {
    if (!result || typeof localStorage === "undefined") return;
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ goalKcal: result.goalKcal, savedAt: Date.now() }),
      );
    } catch {
      /* ignore */
    }
  }, [result?.goalKcal]);

  const errKey = !validation.ok ? validation.error : null;
  const ageError = errKey === "age_invalid" ? tr("err_age") : null;
  const heightError = errKey === "height_invalid" ? tr("err_height") : null;
  const weightError = errKey === "weight_invalid" ? tr("err_weight") : null;
  const bfError = errKey === "bf_invalid" ? tr("err_bf") : null;

  const reset = () => {
    setUnit("metric");
    setGender("male");
    setAge("30");
    setCmStr("170");
    setKgStr("65");
    setFt("5"); setInch("7"); setLb(kgToLb(65).toFixed(1));
    setHeightCm(170); setWeightKg(65);
    setActivity("moderate");
    setGoal("maintain");
    setFormula("mifflin");
    setBodyFat("20");
  };

  const shareLine = result
    ? `${tr("daily")}: ${fmt(result.goalKcal)} ${tr("kcal")}`
    : "";

  const macroData = result
    ? [
        { name: tr("carbs"), value: result.macros.carbsKcal, color: "hsl(var(--accent))" },
        { name: tr("protein"), value: result.macros.proteinKcal, color: "hsl(220 70% 55%)" },
        { name: tr("fat"), value: result.macros.fatKcal, color: "hsl(35 85% 55%)" },
      ]
    : [];

  return (
    <CalculatorLayout
      slug="calorie-calculator"
      title={tr("h1")}
      description={tr("desc")}
      iconName="Flame"
      faq={FAQ.map((f) => ({ q: f.q[lang], a: f.a[lang] }))}
    >
      {/* Top toggles */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <UnitToggle<UnitSystem>
          label={tr("units")}
          value={unit}
          onChange={switchUnit}
          options={[
            { value: "metric", label: tr("metric") },
            { value: "imperial", label: tr("imperial") },
          ]}
        />
      </div>

      {/* Inputs */}
      <div className="mt-5 grid gap-5 md:grid-cols-2">
        <div className="space-y-1.5">
          <label className={cn("block text-sm font-medium", lang === "bn" && "font-bn")}>
            {tr("gender")}
          </label>
          <div className="inline-flex rounded-lg border border-border bg-muted p-0.5">
            {(["male", "female"] as Gender[]).map((g) => {
              const active = g === gender;
              return (
                <button
                  key={g}
                  type="button"
                  onClick={() => setGender(g)}
                  className={cn(
                    "rounded-md px-3 py-1.5 text-sm font-medium transition",
                    active ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground",
                    lang === "bn" && "font-bn",
                  )}
                >
                  {g === "male" ? tr("male") : tr("female")}
                </button>
              );
            })}
          </div>
        </div>

        <NumberInput
          label={tr("age")}
          value={age}
          onChange={setAge}
          unit={tr("ageUnit")}
          error={ageError}
          min={15}
          max={100}
        />

        {unit === "metric" ? (
          <NumberInput
            label={tr("height")}
            value={cmStr}
            onChange={setCmStr}
            unit={tr("cm")}
            error={heightError}
            placeholder="170"
          />
        ) : (
          <div className="space-y-1.5">
            <label className={cn("block text-sm font-medium", lang === "bn" && "font-bn")}>
              {tr("height")}
            </label>
            <div className="grid grid-cols-2 gap-3">
              <NumberInput label="" value={ft} onChange={setFt} unit={tr("ft")} />
              <NumberInput label="" value={inch} onChange={setInch} unit={tr("inch")} />
            </div>
            {heightError && (
              <p className={cn("text-xs text-destructive", lang === "bn" && "font-bn")}>{heightError}</p>
            )}
          </div>
        )}

        {unit === "metric" ? (
          <NumberInput
            label={tr("weight")}
            value={kgStr}
            onChange={setKgStr}
            unit={tr("kg")}
            error={weightError}
            placeholder="65"
          />
        ) : (
          <NumberInput
            label={tr("weight")}
            value={lb}
            onChange={setLb}
            unit={tr("lb")}
            error={weightError}
            placeholder="143"
          />
        )}
      </div>

      {/* Activity */}
      <div className="mt-6 space-y-2">
        <label className={cn("block text-sm font-medium", lang === "bn" && "font-bn")}>
          {tr("activity")}
        </label>
        <div className="grid gap-2 md:grid-cols-2">
          {(Object.keys(ACTIVITY_LABELS) as Activity[]).map((a) => {
            const meta = ACTIVITY_LABELS[a];
            const active = a === activity;
            return (
              <button
                key={a}
                type="button"
                onClick={() => setActivity(a)}
                className={cn(
                  "flex items-start justify-between gap-3 rounded-lg border px-3 py-2.5 text-left transition",
                  active
                    ? "border-accent bg-accent/5"
                    : "border-border bg-card hover:border-accent/40",
                )}
              >
                <div className="min-w-0">
                  <p className={cn("text-sm font-semibold", lang === "bn" && "font-bn")}>{meta.name[lang]}</p>
                  <p className={cn("text-xs text-muted-foreground", lang === "bn" && "font-bn")}>{meta.desc[lang]}</p>
                </div>
                <span className="shrink-0 rounded-md bg-muted px-1.5 py-0.5 text-[10px] tabular-nums text-muted-foreground">
                  ×{num(meta.mult)}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Goal */}
      <div className="mt-6 space-y-2">
        <label className={cn("block text-sm font-medium", lang === "bn" && "font-bn")}>
          {tr("goal")}
        </label>
        <div className="grid gap-2 md:grid-cols-2">
          {(Object.keys(GOAL_LABELS) as Goal[]).map((g) => {
            const active = g === goal;
            return (
              <button
                key={g}
                type="button"
                onClick={() => setGoal(g)}
                className={cn(
                  "rounded-lg border px-3 py-2 text-left text-sm transition",
                  active ? "border-accent bg-accent/5 font-semibold" : "border-border bg-card hover:border-accent/40",
                  lang === "bn" && "font-bn",
                )}
              >
                {GOAL_LABELS[g][lang]}
              </button>
            );
          })}
        </div>
      </div>

      {/* Formula */}
      <div className="mt-6 grid gap-3 md:grid-cols-2">
        <div className="space-y-1.5">
          <label className={cn("block text-sm font-medium", lang === "bn" && "font-bn")}>
            {tr("formula")}
          </label>
          <select
            value={formula}
            onChange={(e) => setFormula(e.target.value as Formula)}
            className={cn(
              "w-full rounded-lg border border-border bg-muted px-3 py-2.5 text-sm outline-none focus:bg-background focus:ring-2 focus:ring-accent",
              lang === "bn" && "font-bn",
            )}
          >
            {(Object.keys(FORMULA_LABELS) as Formula[]).map((f) => (
              <option key={f} value={f}>{FORMULA_LABELS[f][lang]}</option>
            ))}
          </select>
        </div>
        {formula === "katch" && (
          <NumberInput
            label={tr("bodyFat")}
            value={bodyFat}
            onChange={setBodyFat}
            unit="%"
            error={bfError}
            min={3}
            max={60}
          />
        )}
      </div>

      <div className="mt-5 flex justify-end">
        <Button variant="outline" onClick={reset} className="gap-2">
          <RotateCcw size={14} />
          {tr("reset")}
        </Button>
      </div>

      {/* Result */}
      {result && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="mt-8 space-y-6"
        >
          {/* Primary */}
          <div className="rounded-xl border border-accent/30 bg-accent/5 p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className={cn("text-xs font-semibold uppercase tracking-wide text-accent", lang === "bn" && "font-bn")}>
                  {tr("daily")}
                </p>
                <p className="mt-1 flex items-baseline gap-2 text-4xl font-semibold tabular-nums text-foreground md:text-5xl">
                  <Flame size={28} className="text-accent" />
                  {fmt(result.goalKcal)}
                  <span className={cn("text-sm font-normal text-muted-foreground", lang === "bn" && "font-bn")}>
                    {tr("kcal")}
                  </span>
                </p>
                <p className={cn("mt-2 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
                  {GOAL_LABELS[goal][lang]}
                </p>
              </div>
              <CopyResultButton text={shareLine} />
            </div>
          </div>

          {/* BMR / TDEE breakdown */}
          <div className="grid gap-3 md:grid-cols-3">
            <Stat label={tr("bmr")} value={`${fmt(result.bmr)} ${tr("kcal")}`} lang={lang} />
            <Stat label={tr("tdee")} value={`${fmt(result.tdee)} ${tr("kcal")}`} lang={lang} />
            <Stat label={tr("activityKcal")} value={`${fmt(result.activityKcal)} ${tr("kcal")}`} lang={lang} />
          </div>

          {/* Macros */}
          <div className="rounded-xl border border-border bg-card p-5">
            <h3 className={cn("mb-3 text-sm font-bold", lang === "bn" && "font-bn")}>{tr("macros")}</h3>
            <div className="grid items-center gap-5 md:grid-cols-[1fr_1.2fr]">
              <div className="h-48 w-full">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={macroData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={2}>
                      {macroData.map((d) => (<Cell key={d.name} fill={d.color} />))}
                    </Pie>
                    <Tooltip
                      formatter={(v: number, n: string) => [`${fmt(v)} ${tr("kcal")}`, n]}
                      contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="overflow-hidden rounded-lg border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/60 text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className={cn("px-3 py-2 text-left", lang === "bn" && "font-bn")}>{tr("macros")}</th>
                      <th className="px-3 py-2 text-right">{tr("grams")}</th>
                      <th className="px-3 py-2 text-right">{tr("kcal")}</th>
                      <th className="px-3 py-2 text-right">%</th>
                    </tr>
                  </thead>
                  <tbody>
                    <MacroRow lang={lang} name={tr("carbs")} g={result.macros.carbsG} kcal={result.macros.carbsKcal} pct={result.macroSplit.carbsPct} num={num} />
                    <MacroRow lang={lang} name={tr("protein")} g={result.macros.proteinG} kcal={result.macros.proteinKcal} pct={result.macroSplit.proteinPct} num={num} />
                    <MacroRow lang={lang} name={tr("fat")} g={result.macros.fatG} kcal={result.macros.fatKcal} pct={result.macroSplit.fatPct} num={num} />
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Water */}
          <div className="flex items-start gap-3 rounded-xl border border-border bg-card p-4 text-sm">
            <Info size={16} className="mt-0.5 shrink-0 text-accent" />
            <p className={cn(lang === "bn" && "font-bn")}>
              <span className="font-semibold">{tr("water")}: </span>
              <span className="tabular-nums">{num((result.waterMlBase / 1000).toFixed(1))} L</span>
              <span className="text-muted-foreground"> — {tr("waterNote")} → {num((result.waterMlHotClimate / 1000).toFixed(1))} L</span>
            </p>
          </div>

          {/* Warnings */}
          {result.warnings.length > 0 && (
            <div className="space-y-2">
              {result.warnings.map((w) => (
                <div key={w} className={cn(
                  "flex items-start gap-3 rounded-xl border border-yellow-400/40 bg-yellow-400/5 p-3 text-xs",
                  lang === "bn" && "font-bn",
                )}>
                  <AlertTriangle size={16} className="mt-0.5 shrink-0 text-yellow-500" />
                  <p>{WARNING_TEXT[w][lang]}</p>
                </div>
              ))}
            </div>
          )}

          {/* Disclaimer */}
          <div className={cn(
            "flex items-start gap-3 rounded-xl border border-border bg-muted/40 p-4 text-xs text-foreground/80",
            lang === "bn" && "font-bn",
          )}>
            <AlertTriangle size={16} className="mt-0.5 shrink-0 text-muted-foreground" />
            <p>{tr("disclaimer")}</p>
          </div>

          <div className="flex justify-end">
            <ShareButton title={shareLine} />
          </div>
        </motion.div>
      )}

      {/* Educational content */}
      <article className="mt-12 space-y-10">
        <Section title={lang === "bn" ? "ক্যালরি কী?" : "What is a calorie?"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "bn"
              ? "ক্যালরি হলো শক্তির একক। খাবারে থাকা ক্যালরি দিয়ে আমাদের শরীর শ্বাস-প্রশ্বাস, রক্ত সঞ্চালন, পেশী চলাচল ও মস্তিষ্কের কাজ চালায়। দৈনিক চাহিদা থেকে বেশি খেলে ওজন বাড়ে, কম খেলে কমে।"
              : "A calorie is a unit of energy. The calories in food fuel everything your body does — breathing, circulation, muscle movement, and brain activity. Eat more than you burn and you gain weight; eat less and you lose."}
          </p>
        </Section>

        <Section title={lang === "bn" ? "বিএমআর বনাম TDEE" : "BMR vs TDEE"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "bn"
              ? "BMR (বেসাল মেটাবলিক রেট) হলো বিশ্রামে আপনার শরীরের ন্যূনতম শক্তি প্রয়োজন। TDEE (টোটাল ডেইলি এনার্জি এক্সপেন্ডিচার) এর সঙ্গে দৈনন্দিন কাজ ও ব্যায়ামে খরচ হওয়া ক্যালরি যোগ করে। ওজন কমাতে TDEE থেকে ২৫০–৫০০ কিলোক্যালরি কম খান।"
              : "BMR (Basal Metabolic Rate) is the minimum energy your body needs at rest. TDEE (Total Daily Energy Expenditure) adds the calories you burn through daily activity and exercise. To lose weight, eat 250–500 kcal below your TDEE."}
          </p>
        </Section>

        <Section title={lang === "bn" ? "সক্রিয়তা মাল্টিপ্লায়ার" : "Activity multipliers"} lang={lang}>
          <div className="overflow-hidden rounded-xl border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/60 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className={cn("px-3 py-2 text-left", lang === "bn" && "font-bn")}>{tr("activity")}</th>
                  <th className="px-3 py-2 text-right">×</th>
                </tr>
              </thead>
              <tbody>
                {(Object.keys(ACTIVITY_LABELS) as Activity[]).map((a) => (
                  <tr key={a} className="border-t border-border">
                    <td className={cn("px-3 py-2", lang === "bn" && "font-bn")}>
                      {ACTIVITY_LABELS[a].name[lang]} <span className="text-xs text-muted-foreground">— {ACTIVITY_LABELS[a].desc[lang]}</span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{num(ACTIVITY_MULTIPLIER[a])}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        <Section title={lang === "bn" ? "বাংলাদেশী খাবারের ক্যালরি" : "Common BD foods — calorie reference"} lang={lang}>
          <div className="overflow-hidden rounded-xl border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/60 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className={cn("px-3 py-2 text-left", lang === "bn" && "font-bn")}>{lang === "bn" ? "খাবার" : "Food"}</th>
                  <th className={cn("px-3 py-2 text-left", lang === "bn" && "font-bn")}>{lang === "bn" ? "পরিবেশন" : "Serving"}</th>
                  <th className="px-3 py-2 text-right">kcal</th>
                </tr>
              </thead>
              <tbody>
                {BD_FOODS.map((f, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className={cn("px-3 py-2", lang === "bn" && "font-bn")}>{f.name[lang]}</td>
                    <td className={cn("px-3 py-2 text-muted-foreground", lang === "bn" && "font-bn")}>{f.serving[lang]}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{num(f.kcal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        <Section title={lang === "bn" ? "নিরাপদ ওজন কমানোর গতি" : "Healthy weight loss pace"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "bn"
              ? "সপ্তাহে ০.৫–১ কেজি পর্যন্ত কমানো নিরাপদ। এর বেশি দ্রুত কমালে পেশী ক্ষয়, পুষ্টি ঘাটতি, ক্লান্তি ও ইটিং ডিসঅর্ডারের ঝুঁকি বাড়ে। অতিরিক্ত কম-ক্যালরি ডায়েট (নারী ১,২০০ কিলোক্যালরির নিচে, পুরুষ ১,৫০০ এর নিচে) চিকিৎসা তত্ত্বাবধান ছাড়া করবেন না।"
              : "Losing 0.5–1 kg per week is considered safe. Faster loss risks muscle loss, nutrient deficiency, fatigue, and disordered eating. Never go below 1,200 kcal (women) or 1,500 kcal (men) without medical supervision."}
          </p>
        </Section>

        <Section title={lang === "bn" ? "দাবিত্যাগ" : "Disclaimer"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>{tr("disclaimer")}</p>
        </Section>
      </article>
    </CalculatorLayout>
  );
}

/* ------------ Helpers ------------ */

function Stat({ label, value, lang }: { label: string; value: string; lang: "en" | "bn" }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <p className={cn("text-xs font-medium text-muted-foreground", lang === "bn" && "font-bn")}>{label}</p>
      <p className={cn("mt-1 text-lg font-semibold tabular-nums", lang === "bn" && "font-bn")}>{value}</p>
    </div>
  );
}

function MacroRow({
  lang, name, g, kcal, pct, num,
}: { lang: "en" | "bn"; name: string; g: number; kcal: number; pct: number; num: (n: number | string) => string }) {
  return (
    <tr className="border-t border-border">
      <td className={cn("px-3 py-2", lang === "bn" && "font-bn")}>{name}</td>
      <td className="px-3 py-2 text-right tabular-nums">{num(g)}</td>
      <td className="px-3 py-2 text-right tabular-nums">{num(kcal)}</td>
      <td className="px-3 py-2 text-right tabular-nums">{num(pct)}%</td>
    </tr>
  );
}

function Section({ title, children, lang }: { title: string; children: React.ReactNode; lang: "en" | "bn" }) {
  return (
    <section>
      <h2 className={cn("mb-3 text-lg font-bold tracking-tight", lang === "bn" && "font-bn")}>{title}</h2>
      {children}
    </section>
  );
}

// Suppress unused-warning for re-exported constants (kept for related-calc parity)
void KG_PER_LB; void CM_PER_IN;
